// The conversation, once — shared by the EA home and the shell dock (#331).
//
// WHY A HOOK AND NOT TWO COMPONENTS
// =================================
// The contract's whole premise is that there is ONE conversation: Cipher chat
// and Telegram are "two entrances to the same EA", meeting at
// `channels.turn()`. A dock that kept its own message list would be a third
// entrance with its own idea of what was said, and the first time the operator
// opened the EA home after using the dock they would see a different history.
// So both surfaces mount this, and both read the server's transcript.
//
// WHAT IT REFUSES TO DO
// =====================
// It does not invent an assistant reply. If the turn comes back without one —
// the backend returns `replied: false` when the assistant is dormant, and
// `session.UNAVAILABLE` when the model is unreachable — that is what the
// operator sees. A hopeful "…" that never resolves is how a surface starts
// lying about whether anything happened.
//
// It does not retry. A failed turn is reported with its reason and the text is
// handed back to the composer so nothing the operator typed is lost.
import { useCallback, useEffect, useRef, useState } from "react";

import { get, post, postStream, ENDPOINTS } from "./api";

/** Server turn rows -> the shape the UI renders. */
function toMessages(turns) {
  return (turns || []).map((t, i) => ({
    id: t.conversation_id || `turn-${i}`,
    who: t.direction === "outbound" ? "assistant" : "owner",
    text: t.text || "",
    channel: t.channel || null,
    at: t.created_at || null,
    // One-tap answers, carried through ONLY when the turn actually has them.
    // Nothing writes this field today, so none appear — the surface is not
    // allowed to invent them by reading her prose for a question mark.
    choices: Array.isArray(t.choices) ? t.choices : null,
  }));
}

export default function useConversation({ limit = 40, enabled = true } = {}) {
  // "loading" | "ok" | "disabled" | "denied" | "error"
  const [status, setStatus] = useState(enabled ? "loading" : "idle");
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  // The reply so far, while it is still being written. Never persisted.
  const [streamingText, setStreamingText] = useState("");
  const [error, setError] = useState(null);
  // A turn the assistant did not answer, so the surface can say why rather
  // than showing a gap where a reply should be.
  const [notLive, setNotLive] = useState(false);
  const alive = useRef(true);

  useEffect(() => () => { alive.current = false; }, []);

  const load = useCallback(async () => {
    if (!enabled) return;
    const res = await get(`${ENDPOINTS.conversation}?limit=${limit}`);
    if (!alive.current) return;
    if (res.status === "ok") {
      setMessages(toMessages(res.data?.turns));
      setStatus("ok");
    } else {
      setStatus(res.status);
      setError(res.error || null);
    }
  }, [enabled, limit]);

  useEffect(() => { load(); }, [load]);

  const send = useCallback(async (raw) => {
    const text = (raw || "").trim();
    if (!text || sending) return { ok: false };
    setSending(true);
    setError(null);

    // Shown immediately with a pending mark. It is replaced by the server's
    // own transcript on reload, so what stays on screen is what was stored.
    const optimisticId = `pending-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      { id: optimisticId, who: "owner", text, pending: true },
    ]);

    // STREAMED FIRST (#331). Her words land as she writes them; the partial
    // reply is held in `streamingText` and rendered as a live turn, then
    // replaced by the server's own transcript on reload — so what remains on
    // screen is always what was stored, never what we accumulated.
    //
    // The plain endpoint is the fallback, not a second implementation: both
    // reach `channels`, write the same two rows and run the same capture.
    let res = await postStream(ENDPOINTS.chatStream, { text }, (piece) => {
      if (!alive.current) return;
      setStreamingText((prev) => prev + piece);
    });
    if (res.status === "unsupported") {
      res = await post(ENDPOINTS.chat, { text });
    }
    if (!alive.current) return { ok: false };
    setSending(false);
    setStreamingText("");

    // Checked BEFORE the generic error branch: the streamed path reports
    // dormancy as an error WITH a reason, and "your assistant is asleep" is a
    // different thing to tell someone than "the request failed".
    if (res.reason === "ea_not_live") {
      setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      setNotLive(true);
      return { ok: false, restore: text, notLive: true };
    }

    if (res.status !== "ok") {
      // Drop the optimistic row and hand the text back — the composer refills
      // so the operator does not retype it.
      setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      setError(res.error || "Your message could not be sent.");
      return { ok: false, restore: text };
    }

    // Not live for this owner. The streamed path says so with a reason; the
    // plain one with `handled: false`. The message was not stored either
    // way, so the optimistic row goes too.
    if (res.data && res.data.handled === false) {
      setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      setNotLive(true);
      return { ok: false, restore: text, notLive: true };
    }

    await load();
    return { ok: true };
  }, [sending, load]);

  return { status, messages, sending, streamingText, error, notLive, send,
           reload: load };
}
